import { Button } from '@mui/material';
import { AxiosError } from 'axios';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { includes, map, transform } from 'lodash';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import * as Yup from 'yup';

import { shopsApi } from 'api';
import {
  DataWrapper,
  FormActions,
  FormControls,
  FormikCheckbox,
  FormikDirtyGuard,
  FormikRadioGroup,
  FormikSecretField,
  FormikSelect,
  FormikSelectOption,
  usePostSavePromptSafe,
  useUnsavedChangesGuardSafe,
} from 'components';
import { QueryKey, ShopIntegrationSchema, ShopIntegrationType } from 'enums';
import { useMutation, useShopIntegrationTypesConfig } from 'hooks';
import { ShopIntegrationConfig } from 'types';

type Props = {
  shopId: string;
  navigateBack: () => void;
};

type Values = ShopIntegrationConfig;

export const ShopIntegrations: React.FC<Props> = ({ shopId, navigateBack }) => {
  const { t: tCommon } = useTranslation();

  const { onSavedSuccess } = usePostSavePromptSafe();
  const { confirmIfDirty } = useUnsavedChangesGuardSafe();
  const handleBack = useCallback(
    () => confirmIfDirty(navigateBack),
    [confirmIfDirty, navigateBack],
  );

  const { availableShopIntegrationTypes } = useShopIntegrationTypesConfig();

  const integrationSchemaOptions = useMemo(
    () =>
      map(Object.values(ShopIntegrationSchema), (schema) => ({
        value: schema,
        label: tCommon(`shop_integration_config.schema.${schema}`),
      })),
    [tCommon],
  );

  const integrationTypeOptions = useMemo(
    () =>
      transform(
        Object.values(ShopIntegrationType),
        (acc, type) => {
          if (includes(availableShopIntegrationTypes, type)) {
            acc.push({
              value: type,
              label: tCommon(`shop_integration_config.types.${type}`),
            });
          }
        },
        [] as FormikSelectOption[],
      ),
    [tCommon, availableShopIntegrationTypes],
  );

  const queryClient = useQueryClient();

  const [initialValues, setInitialValues] = useState<Values>({
    enabled: false,
    type: '' as ShopIntegrationType,
    schema: undefined,
    publicKey: '',
    privateKey: '',
  });

  const validationSchema: Yup.ObjectSchema<Values> = useMemo(
    () =>
      Yup.object().shape({
        enabled: Yup.boolean().required(tCommon('errors.required')),
        type: Yup.string()
          .required(tCommon('errors.required'))
          .oneOf(Object.values(ShopIntegrationType)),
        schema: Yup.mixed<ShopIntegrationSchema>().when(['enabled', 'type'], {
          is: (enabled: boolean, type: ShopIntegrationType) =>
            enabled && type === ShopIntegrationType.SQ,
          then: (schema) =>
            schema
              .required(tCommon('errors.required'))
              .oneOf(Object.values(ShopIntegrationSchema)),
          otherwise: (schema) => schema.notRequired(),
        }),
        publicKey: Yup.string().when(['enabled', 'type'], {
          is: (enabled: boolean, type: ShopIntegrationType) =>
            enabled && type !== ShopIntegrationType.BF,
          then: (schema) => schema.required(tCommon('errors.required')),
          otherwise: (schema) => schema.notRequired(),
        }),
        privateKey: Yup.string().when(['enabled', 'type'], {
          is: (enabled: boolean, type: ShopIntegrationType) =>
            enabled && type !== ShopIntegrationType.BF,
          then: (schema) => schema.required(tCommon('errors.required')),
          otherwise: (schema) => schema.notRequired(),
        }),
      }),
    [tCommon],
  );

  const queryResult = useQuery(
    QueryKey.ShopIntegrations,
    shopsApi.getIntegrationConfig(shopId),
    {
      onSuccess: (data) => {
        if (data) {
          setInitialValues({
            enabled: data.enabled,
            type: data.type,
            schema: data.schema,
            publicKey: data.publicKey || '',
            privateKey: data.privateKey || '',
          });
        }
      },
    },
  );
  const { mutate: updateShopIntegrations } = useMutation<
    ShopIntegrationConfig,
    AxiosError,
    {
      shopId: string;
      data: ShopIntegrationConfig;
    },
    unknown
  >(shopsApi.updateIntegrationConfig, {
    mergeNotifierMessages: { success: null },
  });

  const handleSubmit = useCallback(
    (values: Values, helpers: FormikHelpers<Values>) => {
      updateShopIntegrations(
        { shopId, data: values },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(QueryKey.ShopIntegrations);
            onSavedSuccess();
          },
          onSettled: () => helpers.setSubmitting(false),
        },
      );
    },
    [shopId, queryClient, updateShopIntegrations, onSavedSuccess],
  );

  const handleTypeChange = useCallback(
    (formik: FormikProps<Values>, type: ShopIntegrationType) => {
      formik.setFieldValue(
        'schema',
        type !== ShopIntegrationType.SQ
          ? ShopIntegrationSchema.Redirect
          : undefined,
      );
    },
    [],
  );

  const getPublicKeyLabel = useCallback(
    (formik: FormikProps<Values>) =>
      tCommon(
        `shop_integration_config.fields.public_key_labels.${formik.values.type}`,
        { defaultValue: tCommon('shop_integration_config.fields.public_key') },
      ),
    [tCommon],
  );

  return (
    <Formik
      enableReinitialize
      validationSchema={validationSchema}
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <DataWrapper queryResult={queryResult} ignoreState={{ empty: true }}>
          <Fragment>
            <FormikDirtyGuard />
            <Form className="tw-max-w-lg">
              <FormControls>
                <div className="tw-flex">
                  <FormikCheckbox
                    label={tCommon('shop_integration_config.enabled')}
                    name="enabled"
                  />
                </div>

                {formik.values.enabled && (
                  <Fragment>
                    <FormikSelect
                      label={tCommon('shop_integration_config.fields.type')}
                      name="type"
                      options={integrationTypeOptions}
                      autocomplete={false}
                      required
                      onChange={(value) =>
                        handleTypeChange(formik, value as ShopIntegrationType)
                      }
                    />
                    {formik.values.type === ShopIntegrationType.SQ && (
                      <FormikRadioGroup
                        label={tCommon('shop_integration_config.fields.schema')}
                        name="schema"
                        options={integrationSchemaOptions}
                      />
                    )}
                    {formik.values.type !== ShopIntegrationType.BF && (
                      <Fragment>
                        <FormikSecretField
                          label={getPublicKeyLabel(formik)}
                          name="publicKey"
                          required
                        />
                        <FormikSecretField
                          label={tCommon(
                            'shop_integration_config.fields.private_key',
                          )}
                          name="privateKey"
                          required
                        />
                      </Fragment>
                    )}
                  </Fragment>
                )}
              </FormControls>
              <FormActions>
                <Button variant="outlined" onClick={handleBack}>
                  {tCommon('buttons.to_list')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={formik.isSubmitting || !formik.isValid}
                  onClick={formik.submitForm}
                >
                  {tCommon('buttons.save')}
                </Button>
              </FormActions>
            </Form>
          </Fragment>
        </DataWrapper>
      )}
    </Formik>
  );
};
